/**
 * 参数元数据的获取、缓存与离线降级。
 *
 * ## 元数据从哪来
 *
 * ArduPilot **不实现** `COMPONENT_INFORMATION`（核对日期 2026-09-25），
 * 所以唯一的来源是上游随固件发布的参数定义：
 *
 *   `https://autotest.ardupilot.org/Parameters/<机型>/apm.pdef.json`
 *
 * 机型取值：`Copter` / `Plane` / `Rover` / `Sub` / `Blimp` / `AP_Periph`
 * （Copter 那份约 2.24 MB —— 解析代价与策略见 `metadata.ts` 的说明）。
 *
 * ## 三条不能让步的规则
 *
 * 1. **网络可注入。** 这里的 `fetcher` 是一个 `(url) => Promise<...>`，
 *    默认实现才去用 `fetch`。测试里注入固定的小 fixture 就能覆盖全部路径，
 *    不需要联网、也不需要 mock 全局。
 * 2. **缓存键必须带机型与固件版本。** 同一台飞机刷了 4.6 → 4.7 之后，
 *    参数表是会变的（新增/删除/改量纲）。键里没有版本，用户看到的就是上一版固件的说明，
 *    而这类错误没有任何外在症状 —— 界面显示得满满当当，只是全是错的。
 * 3. **拿不到元数据时一切照常工作。** 这是硬要求：离线、机场网络被墙、上游挂掉，
 *    参数下载与读写都必须不受影响，只是没有中文说明与范围校验。
 *    因此 `load()` **永不抛错**，最差返回一个空索引 + 失败原因。
 */
import type { VehicleKind } from '@hgc/ardupilot';

import { IndexedDbStore } from '../persistence/indexeddb';
import { PARAM_METADATA_CACHE_PREFIX, PARAM_METADATA_COLLECTION } from '../persistence/keys';
import { MemoryStore } from '../persistence/memory';
import { DEFAULT_DATABASE_NAME } from '../persistence/persistence';
import type { KeyValueStore, PersistenceCollection } from '../persistence/store';
import {
  createEmptyMetadataIndex,
  createParameterMetadataIndex,
  type ParamMetadataIndex,
  type ParamMetadataSourceStatus,
  type ParamTranslations,
} from './metadata';

// ---------------------------------------------------------------------------
// 机型 → 上游目录名
// ---------------------------------------------------------------------------

export type ParamPlatform = 'Copter' | 'Plane' | 'Rover' | 'Sub' | 'Blimp' | 'AP_Periph';

export const PARAM_PLATFORMS: readonly ParamPlatform[] = [
  'Copter',
  'Plane',
  'Rover',
  'Sub',
  'Blimp',
  'AP_Periph',
];

/**
 * 机型 → pdef 目录名。
 *
 * `tracker` 返回 null：上游没有发布 AntennaTracker 的参数定义目录
 * （`Parameters/` 下只有 Copter/Plane/Rover/Sub/Blimp/AP_Periph）。
 * 返回 null 而不是"猜一个"，因为猜错的结果是给用户看另一台机型的中文说明。
 * `AP_Periph` 不是 `VehicleKind` 里的一员（它是外设固件），只能由调用方显式指定。
 */
export function paramPlatformOf(kind: VehicleKind): ParamPlatform | null {
  switch (kind) {
    case 'copter':
      return 'Copter';
    case 'plane':
      return 'Plane';
    case 'rover':
      return 'Rover';
    case 'sub':
      return 'Sub';
    case 'blimp':
      return 'Blimp';
    default:
      return null;
  }
}

export const PARAM_METADATA_BASE_URL = 'https://autotest.ardupilot.org/Parameters';

export function paramMetadataUrl(platform: ParamPlatform): string {
  return `${PARAM_METADATA_BASE_URL}/${platform}/apm.pdef.json`;
}

// ---------------------------------------------------------------------------
// 网络
// ---------------------------------------------------------------------------

/** 只声明真正用到的那几样，于是测试可以给一个纯对象假实现。 */
export interface ParamFetchResponse {
  readonly ok: boolean;
  readonly status: number;
  readonly statusText?: string;
  text(): Promise<string>;
}

export type ParamFetcher = (url: string) => Promise<ParamFetchResponse>;

/** 默认抓取器：用全局 `fetch`；环境里没有 `fetch` 时抛一个能读懂的错误。 */
export function createFetchParamFetcher(fetchImpl?: ParamFetcher): ParamFetcher {
  const impl =
    fetchImpl ?? (globalThis as unknown as { fetch?: ParamFetcher }).fetch;
  if (impl === undefined) {
    return async () => {
      throw new Error('当前环境没有 fetch，无法获取参数元数据');
    };
  }
  return (url) => impl(url);
}

// ---------------------------------------------------------------------------
// 缓存
// ---------------------------------------------------------------------------

export interface ParamMetadataCacheEntry {
  /** `${机型}@${固件版本}` */
  readonly key: string;
  readonly platform: ParamPlatform;
  readonly firmwareVersion: string;
  /** 原始 JSON 文本。存文本而不是建模结果：读取时仍然走同一套惰性索引，且体积最小。 */
  readonly text: string;
  readonly storedAt: number;
}

export interface ParamMetadataCache {
  read(key: string): Promise<ParamMetadataCacheEntry | null>;
  write(entry: ParamMetadataCacheEntry): Promise<void>;
  remove(key: string): Promise<void>;
  /** 同机型的历史版本键（换固件后要清掉，否则 IndexedDB 会被历次版本堆满） */
  keys(): Promise<readonly string[]>;
  /**
   * 释放底层连接。**可选**是为了不破坏已有的注入实现（内存后端没有连接），
   * 但 IndexedDB 实现必须提供：浏览器里另一个标签页要升级数据库时，
   * 占着旧连接不放会让对面的 `open()` 一直停在 `onblocked`。
   */
  close?(): Promise<void>;
}

export function cacheKeyOf(platform: ParamPlatform, firmwareVersion: string): string {
  const version = firmwareVersion.trim();
  return `${platform}@${version.length === 0 ? 'unknown' : version}`;
}

/**
 * 库里读出来的东西一律当作不可信输入（同 `persistence/keys.ts` 的读取辅助）：
 * 记录可能来自更早的版本、写到一半被打断、或被别的程序覆盖。
 * 重新构造一份而不是把读到的对象直接透出去，多出来的字段就不会流到业务层。
 */
function toCacheEntry(value: unknown): ParamMetadataCacheEntry | null {
  if (typeof value !== 'object' || value === null) return null;
  const record = value as Record<string, unknown>;
  const key = record['key'];
  const platform = record['platform'];
  const firmwareVersion = record['firmwareVersion'];
  const text = record['text'];
  const storedAt = record['storedAt'];
  if (typeof key !== 'string' || typeof text !== 'string' || typeof storedAt !== 'number') return null;
  if (!isParamPlatform(platform) || typeof firmwareVersion !== 'string') return null;
  return { key, platform, firmwareVersion, text, storedAt };
}

function isParamPlatform(value: unknown): value is ParamPlatform {
  return typeof value === 'string' && (PARAM_PLATFORMS as readonly string[]).includes(value);
}

// ---------------------------------------------------------------------------
// 缓存：架在存储抽象上
// ---------------------------------------------------------------------------

/**
 * 存储来源：直接给一个 store，或给一个"异步打开"的工厂。
 *
 * 需要后者是因为 IndexedDB 必须等 `open()` 与迁移跑完才知道有没有后端可用，
 * 而缓存接口本身必须是同步构造的（调用方在 `ParamMetadataService` 的构造里就把它传进来）。
 */
export type KeyValueStoreProvider = KeyValueStore | (() => Promise<KeyValueStore>);

/** 共用的空数组：缓存失败时返回它，调用方不必担心拿到同一个可变数组 */
const EMPTY_KEYS: readonly string[] = Object.freeze([]);

export interface KeyValueMetadataCacheOptions {
  /** 默认 `meta` 集合：与数据库自述、迁移问题清单同一个集合，靠键前缀区分 */
  readonly collection?: PersistenceCollection;
  /** 默认 `pdef/`，见 `persistence/keys.ts` */
  readonly prefix?: string;
}

/**
 * 把元数据缓存架在 {@link KeyValueStore} 上 —— 内存与 IndexedDB **共用这一份实现**，
 * 差别只在后端。
 *
 * 这是这一层的第二个理由（第一个理由是降级）：**存储的版本号与迁移路径只能有一套**。
 * 之前元数据缓存自己 `indexedDB.open('hgc-param-metadata', 1)`，于是库里有第二个数据库、
 * 第二套版本号与第二条迁移路径；升级存储时必然有一边被漏掉，而漏掉的表现是
 * "中文说明有时在、有时不在"—— 不报错、也没有任何日志，属于查不出来的那类缺陷。
 * 现在它只是 `hgc` 库里 `meta` 集合下以 `pdef/` 为前缀的几条记录，
 * 与参数快照、航线草稿、审计日志走同一条迁移链（见 `persistence/schema.ts`）。
 *
 * 键仍然是 `cacheKeyOf()` 的结果，存储里只是多一层常量前缀。**刻意不在这里重拼键格式**：
 * 前缀是常量，剥掉它得到的键与 `read()` / `remove()` 用的键完全一致，
 * 于是"两处各写一份键格式、改一处忘一处"这种漂移在结构上不可能发生。
 *
 * **所有失败都被吞掉**，表现为"没有缓存"：它只值一次 2.2 MB 的下载，
 * 坏了绝不该让参数功能坏掉；而这些失败在真实环境里都是常态
 * （隐私模式、配额满、另一个标签页正在升级数据库）。
 */
export function createKeyValueMetadataCache(
  store: KeyValueStoreProvider,
  options: KeyValueMetadataCacheOptions = {},
): ParamMetadataCache {
  const collection = options.collection ?? PARAM_METADATA_COLLECTION;
  const prefix = options.prefix ?? PARAM_METADATA_CACHE_PREFIX;
  const resolve: () => Promise<KeyValueStore> =
    typeof store === 'function' ? store : () => Promise.resolve(store);

  const attempt = async <T>(
    fallback: T,
    work: (store: KeyValueStore) => Promise<T>,
  ): Promise<T> => {
    try {
      return await work(await resolve());
    } catch {
      return fallback;
    }
  };

  return {
    read: (key) =>
      attempt<ParamMetadataCacheEntry | null>(null, async (store) =>
        toCacheEntry(await store.get(collection, prefix + key)),
      ),
    write: (entry) =>
      attempt(undefined, async (store) => {
        // 键取 `entry.key`，不让调用方另外传一个：两者不一致的话，写进去的记录
        // 永远也读不出来，而且完全没有提示
        await store.put(collection, prefix + entry.key, entry);
        return undefined;
      }),
    remove: (key) =>
      attempt(undefined, async (store) => {
        await store.remove(collection, prefix + key);
        return undefined;
      }),
    keys: () =>
      attempt<readonly string[]>(EMPTY_KEYS, async (store) => {
        const stored = await store.keys(collection, prefix);
        return Object.freeze(stored.map((key) => key.slice(prefix.length)));
      }),
    close: async () => {
      try {
        await (await resolve()).close();
      } catch {
        // 从来没打开过、或已经关掉了：都不需要再释放
      }
    },
  };
}

/**
 * 内存缓存：测试与"没有 IndexedDB 的环境"（node、隐私模式）用。
 *
 * 它就是上面那份实现换个后端（`MemoryStore`），**不是另写一份 Map**：
 * 缓存逻辑只有一处，两种后端的差异就只可能出在存储层。
 */
export function createMemoryMetadataCache(): ParamMetadataCache {
  return createKeyValueMetadataCache(new MemoryStore());
}

export interface IndexedDbMetadataCacheOptions {
  /**
   * 与参数快照、航线草稿、审计日志**共用**的数据库名；默认 `hgc`
   * （`persistence/persistence.ts` 的 `DEFAULT_DATABASE_NAME`）。
   *
   * 早期版本默认成一个独立的 `hgc-param-metadata`：第二个库、第二套版本号（v1）、
   * 第二条迁移路径。现在改成共用，老库里那份缓存会被一次性丢掉
   * （见 `deleteLegacyMetadataDatabase`），代价是下次打开参数页重新下一次 pdef。
   */
  readonly databaseName?: string;
  /** 被别的标签页挡住时的等待上限；默认交给持久化层（5 s） */
  readonly blockedTimeoutMs?: number;
  /** 另一个标签页要升级数据库、本连接被迫关闭时的回调（界面据此提示刷新） */
  readonly onVersionChange?: () => void;
}

/**
 * IndexedDB 缓存。
 *
 * 传入 `factory` 而不是自己去读全局：`@hgc/core` 必须能在没有 DOM 的 node 里被 import，
 * `browserIndexedDbFactory()` 才是读全局的那一处（运行时按需调用）。
 *
 * 打开与迁移**全部交给 `IndexedDbStore`**：这里只剩"懒打开 + 失败后不再重试"。
 * 懒打开是刻意的 —— 模块被 import 时不该碰 IndexedDB（node 里没有它），
 * 而没人在用的缓存连接不该在启动路径上占一个 `open()`。
 */
export function createIndexedDbMetadataCache(
  factory: IDBFactory | null,
  options: IndexedDbMetadataCacheOptions = {},
): ParamMetadataCache {
  const databaseName = options.databaseName ?? DEFAULT_DATABASE_NAME;
  let opened: Promise<KeyValueStore> | null = null;
  let broken = false;

  const open = async (): Promise<KeyValueStore> => {
    if (broken) throw new Error('元数据缓存：这个环境的 IndexedDB 已判定不可用');
    if (factory === null) throw new Error('当前环境没有 IndexedDB');
    opened ??= IndexedDbStore.open({
      name: databaseName,
      factory,
      ...(options.blockedTimeoutMs === undefined
        ? {}
        : { blockedTimeoutMs: options.blockedTimeoutMs }),
      ...(options.onVersionChange === undefined
        ? {}
        : { onVersionChange: options.onVersionChange }),
    });
    try {
      const store = await opened;
      deleteLegacyMetadataDatabase(factory);
      return store;
    } catch (error) {
      // 一次失败就认定这个环境的 IndexedDB 不可用，避免每个参数都去踩同一个坑
      broken = true;
      throw error;
    }
  };

  return createKeyValueMetadataCache(() => open());
}

/**
 * 早期版本用的独立元数据库名。留着只为了**把它删掉**。
 *
 * 不删的后果不是"多一个文件"：它一直占着 2.2 MB 左右的配额（pdef 原文），
 * 而配额压力最后会挤到参数快照与审计日志上 —— 那两样才是用户真正的数据。
 * 删除是**尽力而为**：被别的标签页挡着、工厂没有 `deleteDatabase`、隐私模式，
 * 都只是留着这份旧数据，绝不该让缓存打开失败。
 */
const LEGACY_METADATA_DATABASE_NAME = 'hgc-param-metadata';

function deleteLegacyMetadataDatabase(factory: IDBFactory): void {
  const remove = (factory as { deleteDatabase?: (name: string) => unknown }).deleteDatabase;
  if (typeof remove !== 'function') return;
  try {
    remove.call(factory, LEGACY_METADATA_DATABASE_NAME);
  } catch {
    // 见上：删不掉就留着
  }
}

/**
 * 读全局 `indexedDB`。
 *
 * 刻意做成**函数**而不是模块级常量：模块级读取会让"在 node 里 import `@hgc/core`"直接崩，
 * 而运行时这里的返回 null 只是让缓存退化成内存缓存。
 */
export function browserIndexedDbFactory(): IDBFactory | null {
  const factory = (globalThis as unknown as { indexedDB?: IDBFactory }).indexedDB;
  return factory ?? null;
}

// ---------------------------------------------------------------------------
// 服务
// ---------------------------------------------------------------------------

export interface ParamMetadataServiceOptions {
  readonly platform: ParamPlatform;
  /** 固件版本（参与缓存键）。可以从 `AUTOPILOT_VERSION.flight_sw_version` 解出，未知时传 `'unknown'`。 */
  readonly firmwareVersion: string;
  readonly fetcher?: ParamFetcher;
  readonly cache?: ParamMetadataCache | null;
  readonly now?: () => number;
  readonly translations?: ParamTranslations | null;
  /** 缓存多久算过期；默认 30 天。过期仍会先用缓存（离线优先），再后台刷新。 */
  readonly maxAgeMs?: number;
  /** 后台刷新完成后的通知（界面据此把"（缓存）"标记换掉） */
  readonly onUpdate?: (result: ParamMetadataLoadResult) => void;
}

export interface ParamMetadataLoadResult {
  readonly status: ParamMetadataSourceStatus;
  readonly index: ParamMetadataIndex;
  readonly platform: ParamPlatform;
  readonly firmwareVersion: string;
  readonly url: string;
  /** 用的是哪一份缓存的时间戳；直接来自网络时为 null */
  readonly storedAt: number | null;
  readonly bytes: number;
  /** 解析耗时（毫秒）—— 2 MB 的 JSON 值多少，这里是唯一的实测口径 */
  readonly parseMs: number;
  readonly elapsedMs: number;
  /** 失败原因；成功为 null */
  readonly error: string | null;
  /** 是否已经先返回了缓存、正在后台刷新 */
  readonly refreshing: boolean;
}

const DEFAULT_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;

export class ParamMetadataService {
  readonly options: ParamMetadataServiceOptions;

  private readonly platform: ParamPlatform;
  private readonly firmwareVersion: string;
  private readonly fetcher: ParamFetcher;
  private readonly cache: ParamMetadataCache | null;
  private readonly now: () => number;
  private readonly maxAgeMs: number;

  private result: ParamMetadataLoadResult | null = null;
  private inFlight: Promise<ParamMetadataLoadResult> | null = null;
  private disposed = false;

  constructor(options: ParamMetadataServiceOptions) {
    this.options = options;
    this.platform = options.platform;
    this.firmwareVersion = options.firmwareVersion;
    this.fetcher = options.fetcher ?? createFetchParamFetcher();
    this.cache = options.cache ?? null;
    this.now = options.now ?? (() => Date.now());
    this.maxAgeMs = options.maxAgeMs ?? DEFAULT_MAX_AGE_MS;
  }

  /** 已经拿到手的索引（还没加载过时为 null） */
  get current(): ParamMetadataIndex | null {
    return this.result?.index ?? null;
  }

  get lastResult(): ParamMetadataLoadResult | null {
    return this.result;
  }

  get url(): string {
    return paramMetadataUrl(this.platform);
  }

  /**
   * 加载元数据。**永不抛错**：最差返回 `status: 'empty'` 的空索引。
   *
   * - 已经有结果且未 `force` → 直接返回（同一台飞机的参数面板反复打开不该反复读盘/联网）
   * - 并发调用共享同一次加载
   * - 有**未过期**的缓存 → 直接用缓存，不联网
   * - 有**已过期**的缓存 → 先把缓存给你（离线优先，界面立刻可用），再后台刷新
   * - 没有缓存 → 联网；失败则返回空索引并带上失败原因（离线降级）
   */
  async load(options: { readonly force?: boolean } = {}): Promise<ParamMetadataLoadResult> {
    const force = options.force ?? false;
    if (!force && this.result !== null) return this.result;
    if (this.inFlight !== null) return this.inFlight;

    const attempt = this.attempt(force).finally(() => {
      this.inFlight = null;
    });
    this.inFlight = attempt;
    return attempt;
  }

  /** 强制联网刷新一次（界面上的"更新参数说明"按钮）。失败时仍然回退到缓存。 */
  async refresh(): Promise<ParamMetadataLoadResult> {
    this.inFlight = null;
    return this.load({ force: true });
  }

  dispose(): void {
    this.disposed = true;
  }

  private async attempt(force: boolean): Promise<ParamMetadataLoadResult> {
    const startedAt = this.now();
    const key = cacheKeyOf(this.platform, this.firmwareVersion);
    const cached = await this.readCache(key);
    const cachedAge = cached === null ? null : Math.max(0, startedAt - cached.storedAt);
    const cachedIsFresh = cachedAge !== null && cachedAge <= this.maxAgeMs;

    if (!force && cached !== null && cachedIsFresh) {
      const result = this.buildResult({
        status: 'cache',
        text: cached.text,
        storedAt: cached.storedAt,
        startedAt,
        error: null,
        refreshing: false,
      });
      this.result = result;
      return result;
    }

    // 有缓存但过期：先给可用的旧数据，同时后台刷新。用户不该为了"元数据的 30 天保鲜期"
    // 在打开参数页时等 2.2 MB 的下载。
    if (!force && cached !== null) {
      const result = this.buildResult({
        status: 'stale-cache',
        text: cached.text,
        storedAt: cached.storedAt,
        startedAt,
        error: null,
        refreshing: true,
      });
      this.result = result;
      this.refreshInBackground();
      return result;
    }

    try {
      const text = await this.fetchText();
      await this.writeCache(key, text);
      const result = this.buildResult({
        status: 'network',
        text,
        storedAt: null,
        startedAt,
        error: null,
        refreshing: false,
      });
      this.result = result;
      return result;
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      if (cached !== null) {
        const result = this.buildResult({
          status: cachedIsFresh ? 'cache' : 'stale-cache',
          text: cached.text,
          storedAt: cached.storedAt,
          startedAt,
          error: message,
          refreshing: false,
        });
        this.result = result;
        return result;
      }
      const result = this.buildResult({
        status: 'empty',
        text: null,
        storedAt: null,
        startedAt,
        error: message,
        refreshing: false,
      });
      this.result = result;
      return result;
    }
  }

  private async fetchText(): Promise<string> {
    const response = await this.fetcher(this.url);
    if (!response.ok) {
      throw new Error(
        `下载参数元数据失败：HTTP ${response.status}${response.statusText === undefined ? '' : ` ${response.statusText}`}`,
      );
    }
    const text = await response.text();
    if (text.trim().length === 0) throw new Error('下载参数元数据失败：响应为空');
    return text;
  }

  private buildResult(input: {
    readonly status: ParamMetadataSourceStatus;
    readonly text: string | null;
    readonly storedAt: number | null;
    readonly startedAt: number;
    readonly error: string | null;
    readonly refreshing: boolean;
  }): ParamMetadataLoadResult {
    if (input.text === null) {
      return {
        status: 'empty',
        index: createEmptyMetadataIndex(input.error ?? '没有可用的参数元数据'),
        platform: this.platform,
        firmwareVersion: this.firmwareVersion,
        url: this.url,
        storedAt: null,
        bytes: 0,
        parseMs: 0,
        elapsedMs: Math.max(0, this.now() - input.startedAt),
        error: input.error,
        refreshing: false,
      };
    }

    const parseStartedAt = this.now();
    let index: ParamMetadataIndex;
    try {
      index = createParameterMetadataIndex(input.text, {
        source: input.status,
        ...(this.options.translations === undefined || this.options.translations === null
          ? {}
          : { translations: this.options.translations }),
        ...(input.error === null ? {} : { unavailableReason: input.error }),
      });
    } catch (error) {
      // 缓存里可能躺着半截文本（浏览器在写 IndexedDB 时被关掉），
      // 这时必须降级而不是抛错
      const message = error instanceof Error ? error.message : String(error);
      return {
        status: 'empty',
        index: createEmptyMetadataIndex(message),
        platform: this.platform,
        firmwareVersion: this.firmwareVersion,
        url: this.url,
        storedAt: input.storedAt,
        bytes: input.text.length,
        parseMs: Math.max(0, this.now() - parseStartedAt),
        elapsedMs: Math.max(0, this.now() - input.startedAt),
        error: message,
        refreshing: false,
      };
    }
    const parseMs = Math.max(0, this.now() - parseStartedAt);

    return {
      status: input.status,
      index,
      platform: this.platform,
      firmwareVersion: this.firmwareVersion,
      url: this.url,
      storedAt: input.storedAt,
      bytes: input.text.length,
      parseMs,
      elapsedMs: Math.max(0, this.now() - input.startedAt),
      error: input.error,
      refreshing: input.refreshing,
    };
  }

  private refreshInBackground(): void {
    void this.refresh()
      .then((result) => {
        if (this.disposed) return;
        this.options.onUpdate?.(result);
      })
      .catch(() => {
        // load() 已经保证不抛错，这里只是防御性兜底
      });
  }

  private async readCache(key: string): Promise<ParamMetadataCacheEntry | null> {
    if (this.cache === null) return null;
    try {
      return await this.cache.read(key);
    } catch {
      return null;
    }
  }

  private async writeCache(key: string, text: string): Promise<void> {
    if (this.cache === null) return;
    const entry: ParamMetadataCacheEntry = {
      key,
      platform: this.platform,
      firmwareVersion: this.firmwareVersion,
      text,
      storedAt: this.now(),
    };
    try {
      await this.cache.write(entry);
      // 固件升级后旧版本的元数据永远不会被读到，留着只会把 IndexedDB 堆满
      for (const existing of await this.cache.keys()) {
        if (existing === key) continue;
        if (!existing.startsWith(`${this.platform}@`)) continue;
        await this.cache.remove(existing);
      }
    } catch {
      // 缓存写不进去不影响本次返回
    }
  }
}
